import type { Rng } from '../../core/rng';
import type { FrontType, Lot } from './types';
import type { HouseStreets, StreetFront, StreetKind } from './houseStreets';

/**
 * Which walls of a house's ground floor sell, and whether a fixed luifel shelters its front: on the
 * Warmoesstraat's stretch, and over a timber front that sells. A corner shop opens onto the street
 * beside it as well, unless that is an alley.
 */
export interface ShopFronts {
  readonly front: boolean;
  readonly left: boolean;
  readonly right: boolean;
  readonly luifel: boolean;
}

export const NO_SHOP: ShopFronts = { front: false, left: false, right: false, luifel: false };

/*
 * Lesger's map of the shops in the tax of 1585 (Bulletin KNOB 2015, afb. 2; docs/plans/houses.md, "Shops"),
 * registered onto the lots, gives how densely each street sold relative to the others. A street where it
 * places nine shops or more takes its own share, and a stretch of it its own where the map differs there
 * beyond chance; the rest take their kind's. The tax missed those who did not pay, so the map gives no
 * absolute level: the shares are scaled so that the Nieuwendijk and the Warmoesstraat together keep half
 * their houses selling, the plan's estimate.
 */
export const SHOP = {
  byStreet: {
    /** 0.56 on the map; the houses under the luifels all sell, so the rest of the street sells less. */
    'street-warmoesstraat': 0.5,
    'street-nieuwendijk': 0.55,
    /** The last stretch to the Haarlemmerpoort: no shop on 38 fronts. */
    'street-nieuwendijk-2': 0,
    'square-dam': 0.75,
    'street-sint-jansstraat': 0.9,
    'street-pijlsteeg': 0.45,
    'street-kalverstraat': 0.3,
    'street-nes': 0.2,
    'quay-damrak-west': 0.2,
    'quay-oudezijds-voorburgwal': 0.2,
    'quay-nieuwezijds-voorburgwal': 0.15,
    'lane-singel': 0,
    'lane-kloveniersburgwal-geldersekade': 0,
  } as Readonly<Record<string, number>>,
  byKind: { street: 0.1, square: 0.5, quay: 0.05, shore: 0.2, alley: 0.05, dike: 0, road: 0, path: 0 } satisfies Readonly<
    Record<StreetKind, number>
  >,
  /** The shipbuilders' quarter outside the walls. */
  lastage: 0.05,
  /** A corner stands on two streets' passers-by: twice the busier street's share, within these bounds. */
  corner: { factor: 2, min: 0.3, max: 0.9 },
  /**
   * Merchants met in the open air under the shop awnings of the Warmoesstraat, between the Sint
   * Olofspoort and the Guldehandsteeg (Hell, De Amsterdamse herberg, 2017, for the mid-16th century).
   */
  luifels: { street: 'street-warmoesstraat', between: ['street-guldehandsteeg', 'street-sint-olofspoort'] },
  /** The map has shops in the alleys, which a shop opens onto from its front. */
  frontKinds: ['street', 'square', 'quay', 'shore', 'alley'] satisfies readonly StreetKind[],
  /** A corner shop's second opening goes onto a wider way only: its counter would half block an alley 2.5 m wide. */
  sideKinds: ['street', 'square', 'quay', 'shore'] satisfies readonly StreetKind[],
} as const;

/* A lot's source id is its source's name and a running number, as `street-kalverstraat-2`. */
function sourceName(id: string): string {
  return id.replace(/-\d+$/, '');
}

function streetShare(street: StreetFront): number {
  return SHOP.byStreet[street.id] ?? SHOP.byStreet[sourceName(street.id)] ?? SHOP.byKind[street.kind];
}

export function shopShare(streets: HouseStreets, sourceId: string): number {
  if (!streets.front) return 0;
  const own = sourceId.startsWith('lastage-') && streets.front.kind !== 'alley' ? SHOP.lastage : streetShare(streets.front);
  const sides = [streets.left, streets.right].filter((s): s is StreetFront => s !== null);
  if (sides.length === 0) return own;
  const { factor, min, max } = SHOP.corner;
  return Math.min(max, Math.max(min, factor * Math.max(own, ...sides.map(streetShare))));
}

function opensOnto(way: StreetFront | null, kinds: readonly StreetKind[]): boolean {
  return way !== null && kinds.includes(way.kind);
}

export function onLuifelStretch(streets: HouseStreets): boolean {
  const { street, between } = SHOP.luifels;
  if (!streets.front || sourceName(streets.front.id) !== street || streets.along === null) return false;
  const ends = between.map((id) => streets.junctions.find((j) => j.id === id)?.along);
  if (ends.some((e) => e === undefined)) return false;
  const [from, to] = [Math.min(...(ends as number[])), Math.max(...(ends as number[]))];
  return streets.along > from && streets.along < to;
}

/** From its own fork of the house's stream, so drawing a shop moves no other parameter of any house. */
export function sampleShop(lot: Pick<Lot, 'kind' | 'sourceId'>, streets: HouseStreets, rng: Rng): ShopFronts {
  if (lot.kind !== 'house') return NO_SHOP;
  const luifel = onLuifelStretch(streets);
  const shop = luifel || rng.fork('shop').chance(shopShare(streets, lot.sourceId));
  if (!shop) return NO_SHOP;
  const front = opensOnto(streets.front, SHOP.frontKinds);
  const left = opensOnto(streets.left, SHOP.sideKinds);
  const right = opensOnto(streets.right, SHOP.sideKinds);
  return front || left || right ? { front, left, right, luifel } : NO_SHOP;
}

/*
 * Lesger (S21, p. 71) gives the 16th-century Amsterdam shop as a house with a timber front and a sloping
 * luifel, from the keuren and building history; the keur of about 1413 set the luifels' height. So a timber
 * front that sells keeps one, except onto an alley, where two facing luifels 1.3 m out would meet.
 */
export function withTimberLuifel(shop: ShopFronts, frontType: FrontType, streets: HouseStreets): ShopFronts {
  if (shop.luifel || !shop.front || frontType !== 'timber' || !opensOnto(streets.front, SHOP.sideKinds)) return shop;
  return { ...shop, luifel: true };
}
